import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logo from '@/assets/logo.webp';
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

const ForgotPassword = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        const email = (e.target as any).email.value;

        try {
            // Make the API call to trigger a password reset email
            const response = await fetch(`${import.meta.env.VITE_ADMIN_FORGOT_PASSWORD_ENDPOINT}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                throw new Error("Failed to send reset email");
            }

            toast({
                title: t("forgotPassword.toastSuccessTitle"),
                description: t("forgotPassword.toastSuccessDescription"),
                className: "bg-green-500 text-white border-green-600",
                duration: 5000,
            });
        } catch (error) {
            console.error("Password reset failed:", error);
            toast({
                title: t("forgotPassword.toastErrorTitle"),
                description: t("forgotPassword.toastErrorDescription"),
                variant: "destructive",
                duration: 5000,
            });
        }
        finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        document.title = `${t('forgotPassword.title')} - FitCalendar`;
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <Card className="w-full max-w-md">
                <div className="flex flex-col items-center">
                    <a href="/"><img src={logo} alt="FitCalendar" className="h-6 object-contain mt-8 mb-8" /></a>
                </div>
                <CardHeader>
                    <CardTitle>{t("forgotPassword.title")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email">{t("forgotPassword.emailLabel")}</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder={t("forgotPassword.emailPlaceholder")}
                                required
                            />
                        </div>
                        <Button type="submit" className="w-full">
                            {isSubmitting ? t("forgotPassword.sendingButton") : t("forgotPassword.sendResetLinkButton")}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default ForgotPassword;
